import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../common/components/loader/loader';
import { authRedirectUri } from '../config/env';
import msalConfig from './authConfig';
import { MsalContext } from './msalContext';

export const clientApplication = new PublicClientApplication(msalConfig);

const MSALProvider = ({ children }) => {
  const [msalUserAccount, setMsalUserAccount] = useState(null);
  const [authResult, setAuthResult] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const changeMsalAccount = useCallback((account) => {
    setMsalUserAccount((prev) => {
      const prevId = prev?.homeAccountId ?? null;
      const nextId = account?.homeAccountId ?? null;
      if (prevId === nextId) return prev;
      clientApplication.setActiveAccount(account || null);
      return account;
    });
  }, []);

  const clearAuthResult = useCallback(() => {
    setAuthResult(null);
    setAuthError(null);
  }, []);

  const startLogin = useCallback(async () => {
    setAuthError(null);
    setIsLoading(true);
    await clientApplication.loginRedirect({
      scopes: ['user.read'],
    });
  }, []);

  const startLogout = useCallback(async () => {
    setAuthError(null);
    setIsLoading(true);
    const account = clientApplication.getActiveAccount() || clientApplication.getAllAccounts()[0];

    await clientApplication.logoutRedirect({
      account,
      postLogoutRedirectUri: authRedirectUri || undefined,
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    const bootstrapMsal = async () => {
      try {
        setIsLoading(true);
        if (typeof clientApplication.initialize === 'function') {
          await clientApplication.initialize();
        }

        const response = await clientApplication.handleRedirectPromise();
        if (!mounted) return;

        if (response?.account) {
          changeMsalAccount(response.account);
          setAuthResult(response);
        } else {
          const existingAccount =
            clientApplication.getActiveAccount() || clientApplication.getAllAccounts()[0] || null;
          changeMsalAccount(existingAccount);
        }
      } catch (error) {
        if (mounted) {
          setAuthError(error);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
          setIsReady(true);
        }
      }
    };

    const callbackId = clientApplication.addEventCallback((event) => {
      if (!mounted) return;

      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        if (event.payload?.account) {
          changeMsalAccount(event.payload.account);
        }
        if (event.payload) {
          setAuthResult(event.payload);
        }
        setAuthError(null);
        setIsLoading(false);
      }

      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        changeMsalAccount(null);
        setAuthResult(null);
        setAuthError(null);
        setIsLoading(false);
      }

      if (
        event.eventType === EventType.LOGIN_FAILURE ||
        event.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
        event.eventType === EventType.LOGOUT_FAILURE
      ) {
        setAuthError(event.error);
        setIsLoading(false);
      }
    });

    bootstrapMsal();

    return () => {
      mounted = false;
      if (callbackId) {
        clientApplication.removeEventCallback(callbackId);
      }
    };
  }, [changeMsalAccount]);

  const value = useMemo(
    () => ({
      msalInstance: clientApplication,
      msalUserAccount,
      authResult,
      authError,
      isLoading,
      isReady,
      changeMsalAccount,
      clearAuthResult,
      startLogin,
      startLogout,
    }),
    [
      authError,
      authResult,
      changeMsalAccount,
      clearAuthResult,
      isLoading,
      isReady,
      msalUserAccount,
      startLogin,
      startLogout,
    ],
  );

  return (
    <MsalContext.Provider value={value}>
      {isReady ? (
        children
      ) : (
        <section className="login">
          <Loader />
        </section>
      )}
    </MsalContext.Provider>
  );
};

export default MSALProvider;
